import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ChartData {
  categories: string[];
  series: { name: string; data: number[] }[];
}

interface ChartConfig {
  horizontal?: boolean,
  stacked?: boolean;
  borderRadius?: number;
  dataLabels?: {
    enabled?: boolean;
    position?: 'top' | 'center' | 'bottom';
    style?: {
      fontSize?: string;
      colors?: string[];
      fontWeight?: string;
    };
  };
  colors?: string[];
  xAxis?: {
    title?: string;
    fontSize?: string;
  };
  yAxis?: {
    title?: string;
  };
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltip?: {
    enabled?: boolean;
    format?: string;
  };
  format?: 'currency' | 'number' | 'percentage';
}

const formatValueShort = (value: number) => {
  if (Math.abs(value) >= 1_000_000) {
    return `${Math.round(value / 1_000_000)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${Math.round(value / 1_000)}K`;
  }
  return Math.round(value).toString();
};

const BarChart = ({ data, config }: { data: ChartData; config: ChartConfig }) => {
  const formatValue = (value: number) => {
    switch (config?.format) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(value);

      case 'percentage':
        return `${value}%`;

      case 'number':
      default:
        if (Math.abs(value) >= 1_000_000) {
          return `${Math.round(value / 1_000_000)}M`;
        }
        if (Math.abs(value) >= 1_000) {
          return `${Math.round(value / 1_000)}K`;
        }
        return Math.round(value).toString();
    }
  };

  const bool = config?.horizontal?.toString().toLowerCase() === "true";
  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: config?.stacked || false,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: bool,
        borderRadius: config?.borderRadius || 0,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => formatValueShort(val),
      style: {
        fontSize: config?.dataLabels?.style?.fontSize || '10px',
        colors: ['#fff'],
        fontWeight: config?.dataLabels?.style?.fontWeight || 'bold'
      }
    },
    colors: config?.colors || ['#1E2D3F', '#4F7CAC'],
    xaxis: {
      categories: data.categories,
      title: {
        text: config?.xAxis?.title || ''
      },
    },
    yaxis: {
      title: {
        text: config?.yAxis?.title || '',
      },
    },
    legend: {
      position: config?.legendPosition || 'top',
    },
    tooltip: {
      enabled: config?.tooltip?.enabled !== false,
      y: {
        formatter: (val: number) => formatValue(val)
      },
      x: {
        formatter: (val: number) => formatValue(val)
      }
    },
  };

  return (
    <ReactApexChart
      options={chartOptions}
      series={data.series}
      type="bar"
      height={350}
    />
  );
};

export default BarChart;